<template>
  <div id="group-details-page"
       class="flex-display-body"
       :class="bodyPadding"
       role="tabpanel"
       aria-labelledby="groupDetails">
    <!--suppress JSUnresolvedVariable onExpirationSelected in renew-group-mixin -->
    <group-renew-modal modal-id="group-expiration-modal" :group-ids="groupIdsToRenew"
                       @groupExpirationSelected="onExpirationSelected"
                       @cancel="groupIdsToRenew = []">
    </group-renew-modal>
    <div class="row">
      <div class="col-12">
        <h2 class="text-center pb-3">Group Details</h2>
        <p id="user-roles">
          <font-awesome-icon icon="info-circle"></font-awesome-icon>
          Your <a href="https://www.middleware.vt.edu/ed/groups/#ougroupsdcvtdcedu"
                                   target="_blank"
                                   rel="noopener noreferrer nofollow">role(s)</a>
          in this group:
          <span><strong>{{authUserRoles}}</strong></span>
        </p>
        <hr>
        <!--General Details-->
        <div class="row">
          <div class="col-md-4">
            <DetailField field-label="Display Name">
              <div id="fld-display-name" v-if="managedGroup.displayName">
                {{managedGroup.displayName}}
              </div>
            </DetailField>
            <hr class="d-block d-md-none">
          </div>
          <div class="col-md-4">
            <DetailField field-label="Group Contact Email Address">
              <div id="fld-email-address" v-if="managedGroup.emailAddress">
                {{ managedGroup.emailAddress }}
              </div>
            </DetailField>
            <hr class="d-block d-md-none">
          </div>
          <div class="col-md-4">
            <DetailField field-label="Expires On">
              <span class="d-flex align-items-center">
                <span :id="'grp-expires-'+dateToMilliseconds(managedGroup.expirationDate)">
                  {{ managedGroup.expirationDate | dateString }}
                </span>
                <b-button v-if="isAdminOfCurrentGroup"
                          v-b-modal.group-expiration-modal
                          size="sm"
                          variant="success"
                          id="btn-renew-group-dashboard"
                          style="margin-left: 5px;"
                          @click="groupIdsToRenew = [managedGroup.uugid]"
                          :disabled="submittingGroupRenew">
                  {{ submittingGroupRenew ? 'Renewing...' : 'Renew' }}
                </b-button>
              </span>
            </DetailField>
          </div>
        </div>
        <hr>
        <!-- Display Administrators -->
        <DetailField field-label="Administrators">
          <div class="row">
            <div v-for="administrator in managedGroup.administrators"
                 :key="`${administrator.roleId}_${administrator.kind}`"
                 class="pad-top col-md-6 col-lg-4">
              <display-role :role="administrator" role-type="administrator"/>
            </div>
          </div>
        </DetailField>
        <hr>
        <!-- Display Managers -->
        <DetailField field-label="Managers" :default-value="listDefault">
          <div v-if="managedGroup.managers.length" class="row">
            <div v-for="manager in managedGroup.managers"
                 :key="`${manager.roleId}_${manager.kind}`"
                 class="pad-top col-md-6 col-lg-4">
              <display-role :role="manager" role-type="manager"/>
            </div>
          </div>
        </DetailField>
        <hr>
        <!-- Display Contacts -->
        <DetailField field-label="Contacts">
          <div class="row">
            <div v-for="contact in managedGroup.contacts"
                 :key="`${contact.roleId}_${contact.kind}`"
                 class="pad-top col-md-6 col-lg-4">
              <display-role :role="contact" role-type="contact"/>
            </div>
          </div>
        </DetailField>
        <hr>
        <!-- Display Viewers -->
        <DetailField field-label="Service Viewers" :default-value="listDefault">
          <div v-if="managedGroup.viewers.length" class="row">
            <div v-for="viewer in managedGroup.viewers"
                 :key="`${viewer.roleId}_${viewer.kind}`"
                 class="pad-top col-md-6 col-lg-4">
              <display-role :role="viewer" role-type="viewer" :show-secondary-display="false"/>
            </div>
          </div>
        </DetailField>
        <hr>
        <!-- Display Members -->
        <DetailField field-label="Members" :default-value="listDefault">
          <div v-if="managedGroup.members.length" class="row">
            <div v-for="member in managedGroup.members"
                 :key="`${member.roleId}_${member.kind}`"
                 class="pad-top col-md-6 col-lg-4">
              <display-role :role="member" role-type="member"/>
            </div>
          </div>
        </DetailField>
      </div>
    </div>
  </div>
</template>

<script>
import { auth } from '../util/groups-mixin';
import { renewGroup } from '../util/renew-group-mixin';
import DetailField from './slots/DetailField';
import DisplayRole from './DisplayRole';

export default {
  name: 'GroupDetail',
  title: 'Group Details',
  mixins: [auth, renewGroup],
  data() {
    return {
      listDefault: 'None',
    };
  },
  props: {
    managedGroup: {
      type: Object,
      required: true,
    },
  },
  computed: {
    authUserRoles() {
      const roles = [];
      if (this.isAdminOfCurrentGroup) {
        roles.push('Administrator');
      }
      if (this.isManagerOfCurrentGroup) {
        roles.push('Manager');
      }
      if (this.managedGroup.isContact) {
        roles.push('Contact');
      }
      if (this.managedGroup.isMember) {
        roles.push('Member');
      }
      return roles.join(' / ');
    },
    /* Padding between the dashboard title and detail component body is not necessary when tabs aren't shown */
    bodyPadding() {
      if (this.isAdminOrManagerOfCurrentGroup) {
        return 'py-5';
      }
      return 'pb-5';
    },
  },
  methods: {
    dateToMilliseconds(value) {
      if (!value) return 'on';
      const date = new Date(value);
      return date.getTime();
    },
    track() {
      this.$gtag.pageview({
        page_title: 'Group Details',
        page_path: `/group/${this.managedGroup.uugid}`,
      });
    },
  },
  filters: {
    dateString(value) {
      if (!value) return;
      const date = new Date(value);
      return date.toDateString();
    },
  },
  activated() {
    this.track();
  },
  components: {
    DetailField,
    DisplayRole,
  },
};
</script>

<style scoped>
  .pad-top {
    padding-top: .5em;
  }
</style>




// WEBPACK FOOTER //
// src/components/GroupDetail.vue